import React, { useState, useEffect } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import queryString from "query-string";
import { apiV2Service } from "../../services/apiV2Service";
import { usePostAuthFlow } from "../../shared/auth";
import logger from "../../utils/logger";
import { showApiMessage } from "../../utils/messageHandler";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../shadcn/ui/card";
import { Input } from "../../shadcn/ui/input";
import { Label } from "../../shadcn/ui/label";
import { Button } from "../../shadcn/ui/button";
import { FaRegEye as FaEye, FaRegEyeSlash as FaEyeSlash } from "react-icons/fa";
import { SpaciaPasswordGuidelines } from "../../components/SpaciaPasswordGuidelines";

const isBase64 = str => {
  if (!str) return false;
  try {
    return btoa(atob(str)) === str;
  } catch (err) {
    return false;
  }
};

const validatePassword = password => {
  const errors = [];
  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }
  // if (!/[A-Z]/.test(password)) {
  //     errors.push("Password must contain at least one uppercase letter");
  // }
  // if (!/[a-z]/.test(password)) {
  //     errors.push("Password must contain at least one lowercase letter");
  // }
  // if (!/[0-9]/.test(password)) {
  //     errors.push("Password must contain at least one number");
  // }
  // if (!/[!@#$%^&*]/.test(password)) {
  //     errors.push("Password must contain at least one special character (!@#$%^&*)");
  // }
  return errors;
};

const SavePassword = () => {
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [resetError, setResetError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const location = useLocation();
  const search = queryString.parse(location.search);
  const params = search ? search.payload : "";

  let payload = null;
  if (isBase64(params)) {
    try {
      payload = JSON.parse(atob(params));
    } catch (e) {
      logger.error("Error parsing Base64 payload:", e);
      setError("Error processing payload.");
    }
  } else if (params) {
    logger.error("Invalid Base64 string:", params);
    setError("Invalid Base64 data.");
  }

  useEffect(() => {
    // First try to get token from URL query parameter (for V2 API)
    const tokenFromQuery = search.token || search.code;
    if (tokenFromQuery) {
      setToken(tokenFromQuery);
      return;
    }

    // Fallback to extracting from base64 payload (for V1 API compatibility)
    if (payload && payload.otp) {
      setToken(payload.otp);
    }
  }, [payload, search]);

  const history = useHistory();
  const { handlePostAuthFlow } = usePostAuthFlow();

  const handlePasswordChange = e => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setValidationErrors(validatePassword(newPassword));
  };

  const activateAccount = async () => {
    if (!password || !confirmPassword) {
      setErrorMsg("Password fields cannot be empty");
      setResetError(true);
      return;
    }

    if (validationErrors.length > 0) {
      setErrorMsg("Please fix all password validation errors");
      setResetError(true);
      return;
    }

    if (password !== confirmPassword) {
      setErrorMsg("Passwords do not match");
      setResetError(true);
      return;
    }

    if (!token) {
      setErrorMsg("Token is missing. Please use the link from your email.");
      setResetError(true);
      return;
    }

    setIsLoading(true);
    setResetError(false);

    try {
      const response = await apiV2Service.resetPasswordWithToken({
        token: token,
        password: password,
      });

      logger.log("Reset password response:", response);
      showApiMessage(response, "save password");

      // If response contains tokens and user, handle post-auth flow
      if (response.data?.access_token && response.data?.refresh_token && response.data?.user) {
        await handlePostAuthFlow({
          user: response.data.user,
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token,
        });
      } else {
        // Fallback to signin page if no tokens
        setTimeout(() => {
          history.push("/signin");
        }, 2000);
      }
    } catch (err) {
      logger.error("Error during API call:", err);
      showApiMessage(err, "save password");

      // Handle specific error cases
      if (err.response?.status === 404) {
        setErrorMsg("Token not found or expired. Please request a new password reset.");
      } else if (err.response?.status === 409) {
        setErrorMsg("This password reset link has already been used. Please request a new one.");
      }
      setResetError(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (error && !validationErrors.length) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        {/* Centralized toast handler used elsewhere; leave error view minimal */}
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-2xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold text-center">Change password</CardTitle>
            <CardDescription className="text-center">Enter your new password to continue</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  placeholder="••••••••••••"
                  onChange={handlePasswordChange}
                  value={password}
                  className={`pr-12 ${validationErrors.length > 0 ? "border-red-500" : ""}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 flex items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                  style={{ transform: "translateY(calc(-50% + 8px))" }}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
                </button>
              </div>
              <SpaciaPasswordGuidelines />
              {validationErrors.length > 0 && (
                <div className="mt-1 text-sm text-red-600 space-y-1">
                  {validationErrors.map((error, index) => (
                    <div key={index}>• {error}</div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  placeholder="••••••••••••"
                  onChange={e => setConfirmPassword(e.target.value)}
                  value={confirmPassword}
                  className={`pr-12 ${password !== confirmPassword && confirmPassword ? "border-red-500" : ""}`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 flex items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                  style={{ transform: "translateY(calc(-50% + 8px))" }}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
                </button>
              </div>
              {password !== confirmPassword && confirmPassword && (
                <div className="mt-1 text-sm text-red-600">Passwords do not match</div>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button onClick={activateAccount} className="w-full" disabled={isLoading || validationErrors.length > 0}>
              {isLoading ? "Changing Password..." : "Change Password"}
            </Button>
            <Link to="/signin" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
              Back to sign in
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default SavePassword;
